/**
 * Bundle level billing
 * --------------------
 * `get-current-plan` returns the billing props (`billingCycle`, `renewalBillingCycle`,
 * `firstBillingCycleUntil`, `expiresOn`) at the bundle level only while the bundle is
 * billed as a whole. When the backend enables order level billing for a bundle it sets
 * bit 12 (`SupportsOrderLevelBilling`) in `bundleState`; those props become null.
 * Billing then lives on each order (`orderDetails[product].expiry` and friends).
 *
 * The control panel supports bundle level billing only, so this module is the single
 * place that decides whether a bundle level billing value may be used at all. Every
 * consumer must treat the returned value as optional.
 *
 * Kept free of imports on purpose: `@/store` reads these getters, and pulling in
 * `helpers/index` here would close an import cycle.
 */

/** The bundle level billing slice of the `get-current-plan` response. */
export type TBundleBilling = {
  /** Set by the backend once billing moved to the individual orders. */
  bundleState?: number | null;
  /** Absent with order level billing. */
  billingCycle?: string | null;
  /** Absent with order level billing. */
  renewalBillingCycle?: string | null;
  /** Absent with order level billing. */
  firstBillingCycleUntil?: string | null;
  /** Absent with order level billing, the order expiry is used instead. */
  expiresOn?: string | null;
};

type TCurrentPlanLike = (TBundleBilling & Record<string, unknown>) | null | undefined;

// BundleFlag bit positions are zero-based: bit 12 has mask 4096.
const SUPPORTS_ORDER_LEVEL_BILLING_MASK = 1 << 12;

export const isOrderLevelBillingEnabled = (currentPlan: TCurrentPlanLike): boolean =>
  ((currentPlan?.bundleState ?? 0) & SUPPORTS_ORDER_LEVEL_BILLING_MASK) !== 0;

/** True while the bundle still carries its own billing props, i.e. they can be read. */
export const hasBundleLevelBilling = (currentPlan: TCurrentPlanLike): boolean =>
  !isOrderLevelBillingEnabled(currentPlan);

const getBundleBillingProp = (
  currentPlan: TCurrentPlanLike,
  prop: keyof TBundleBilling
): string | null => {
  if (!currentPlan || isOrderLevelBillingEnabled(currentPlan)) return null;
  return (currentPlan[prop] as string | undefined) ?? null;
};

/** Billing cycle the bundle is billed on, `null` with order level billing. */
export const getBundleBillingCycle = (currentPlan: TCurrentPlanLike) =>
  getBundleBillingProp(currentPlan, "billingCycle");

/** Billing cycle the bundle renews on, `null` with order level billing. */
export const getBundleRenewalBillingCycle = (currentPlan: TCurrentPlanLike) =>
  getBundleBillingProp(currentPlan, "renewalBillingCycle");

/** Date until which first billing cycle pricing applies, `null` with order level billing. */
export const getBundleFirstBillingCycleUntil = (currentPlan: TCurrentPlanLike) =>
  getBundleBillingProp(currentPlan, "firstBillingCycleUntil");

/** Bundle expiry date, `null` with order level billing - use the order expiry instead. */
export const getBundleExpiresOn = (currentPlan: TCurrentPlanLike) =>
  getBundleBillingProp(currentPlan, "expiresOn");
